import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Container = styled.div`
  padding: 2rem;
  background-color: #f8fafc;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  animation: ${fadeIn} 0.6s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1.5rem;
  }

  @media print {
    background: white;
    padding: 0;
    .no-print {
      display: none;
    }
  }
`;

export const ControlPanel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.5rem;

  .left {
    .badge {
      background: rgba(37, 99, 235, 0.1);
      color: #2563eb;
      font-size: 0.7rem;
      font-weight: 800;
      padding: 0.3rem 0.8rem;
      border-radius: 99px;
      width: fit-content;
      margin-bottom: 0.5rem;
      border: 1px solid rgba(37, 99, 235, 0.2);
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    h1 {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 1.75rem;
      font-weight: 800;
      color: #1e293b;
      margin: 0;
      letter-spacing: -0.5px;
    }
    p {
      color: #64748b;
      margin: 0.2rem 0 0 0;
      font-size: 0.95rem;
    }
  }

  .right {
    display: flex;
    gap: 1rem;
    align-items: center;

    .export-btn {
      background: #1e293b;
      color: white;
      border: none;
      padding: 0.6rem 1.2rem;
      border-radius: 10px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: #0f172a;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
    }
  }
`;

export const SelectGroup = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  padding: 0.5rem 0.8rem;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:focus-within {
    border-color: #2563eb;
    ring: 2px solid rgba(37, 99, 235, 0.2);
  }

  select {
    background: transparent;
    border: none;
    font-weight: 600;
    color: #475569;
    outline: none;
    cursor: pointer;
    font-size: 0.9rem;
  }

  svg {
    color: #64748b;
  }
`;

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;

  .kpi-card {
    background: white;
    padding: 1.5rem;
    border-radius: 16px;
    border: 1px solid #f1f5f9;
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.1);
    }

    .kpi-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &.blue .kpi-icon {
      background: #eff6ff;
      color: #2563eb;
    }
    &.orange .kpi-icon {
      background: #fff7ed;
      color: #ea580c;
    }
    &.purple .kpi-icon {
      background: #faf5ff;
      color: #9333ea;
    }
    &.emerald .kpi-icon {
      background: #ecfdf5;
      color: #059669;
    }

    .kpi-content {
      label {
        font-size: 0.8rem;
        color: #64748b;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      .kpi-value-group {
        display: flex;
        align-items: baseline;
        gap: 0.5rem;

        strong {
          font-size: 1.4rem;
          font-weight: 800;
          color: #1e293b;
        }

        .trend {
          display: flex;
          align-items: center;
          gap: 2px;
          font-size: 0.75rem;
          font-weight: 700;
          padding: 2px 6px;
          border-radius: 6px;

          &.up {
            background: #dcfce7;
            color: #15803d;
          }
          &.down {
            background: #fee2e2;
            color: #b91c1c;
          }
        }
      }
    }
  }
`;

export const MainDashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ReportBox = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 20px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);

  .box-header {
    font-size: 1.1rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;

    &::before {
      content: "";
      width: 4px;
      height: 18px;
      background: #2563eb;
      border-radius: 4px;
      margin-right: 12px;
    }
  }

  .ranking-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    .ranking-item {
      display: flex;
      align-items: center;
      padding: 0.75rem;
      background: #f8fafc;
      border-radius: 12px;
      border: 1px solid #f1f5f9;
      transition: all 0.2s;

      &:hover {
        background: white;
        border-color: #2563eb;
        transform: scale(1.02);
      }

      .rank-number {
        width: 28px;
        height: 28px;
        background: #1e293b;
        color: white;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8rem;
        font-weight: 800;
        margin-right: 1rem;
      }

      .rank-name {
        flex: 1;
        font-weight: 600;
        color: #475569;
        font-size: 0.9rem;
      }

      .rank-value {
        font-weight: 700;
        color: #2563eb;
        font-size: 0.9rem;
      }
    }
  }
`;

export const AlertFooter = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;

  .alert-item {
    flex: 1;
    min-width: 300px;
    background: white;
    padding: 1.25rem;
    border-radius: 16px;
    display: flex;
    align-items: center;
    gap: 1rem;
    border: 1px solid #f1f5f9;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);

    span {
      font-size: 0.9rem;
      color: #64748b;
      line-height: 1.4;

      strong {
        color: #1e293b;
        font-weight: 700;
      }
    }

    &.warning {
      border-left: 5px solid #f59e0b;
      svg {
        color: #f59e0b;
      }
    }

    &.success {
      border-left: 5px solid #10b981;
      svg {
        color: #10b981;
      }
    }
  }
`;

export const LoadingWrapper = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f8fafc;
  gap: 1rem;
  color: #2563eb;
  font-weight: 700;
  font-size: 1.1rem;

  .animate-spin {
    animation: ${spin} 1s linear infinite;
    width: 40px;
    height: 40px;
  }
`;